/**
 * Utility functions for calculating distances between GPS coordinates
 */

// TypeScript declaration for Google Maps Distance Matrix Service
declare global {
  interface Window {
    google: typeof google;
  }
}

export interface DistanceResult {
  roadDistance?: number; // in kilometers (primary - from Google Maps)
  roadDuration?: number; // in minutes (from Google Maps)
  straightLineDistance?: number; // in kilometers (reference only)
  error?: string;
}

/**
 * Wait for Google Maps API to be loaded
 * @param maxWaitTime - Maximum time to wait in milliseconds (default: 10000ms)
 * @returns Promise that resolves when Google Maps is loaded, or rejects after timeout
 */
const waitForGoogleMaps = (maxWaitTime: number = 10000): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded - check both window.google and global google
    const checkLoaded = () => {
      try {
        // Check window.google first (most common) - this is how @react-google-maps/api loads it
        if (typeof window !== 'undefined') {
          const win = window as any;
          if (win.google && win.google.maps && win.google.maps.DirectionsService) {
            console.log('Google Maps API detected on window.google');
            return true;
          }
        }
        // Check global google (for TypeScript/Node environments)
        if (typeof google !== 'undefined' && google.maps && google.maps.DirectionsService) {
          console.log('Google Maps API detected on global google');
          return true;
        }
        return false;
      } catch (e) {
        console.warn('Error checking for Google Maps API:', e);
        return false;
      }
    };

    // Check immediately
    if (checkLoaded()) {
      console.log('Google Maps API already loaded');
      resolve();
      return;
    }

    // Check if script is being loaded
    let scriptLoading = false;
    try {
      const scripts = document.getElementsByTagName('script');
      for (let i = 0; i < scripts.length; i++) {
        if (scripts[i].src && scripts[i].src.includes('maps.googleapis.com')) {
          scriptLoading = true;
          console.log('Google Maps script tag found, waiting for it to load...');
          break;
        }
      }
    } catch (e) {
      // Ignore errors checking scripts
    }

    // If no script is loading and we're in browser, the API might not be initialized yet
    // but @react-google-maps/api should handle this
    if (!scriptLoading && typeof window !== 'undefined') {
      console.log('No Google Maps script found, but will wait in case it loads via useLoadScript');
    }

    const startTime = Date.now();
    const checkInterval = 200; // Check every 200ms

    const checkGoogleMaps = () => {
      if (checkLoaded()) {
        console.log(`Google Maps API loaded successfully after ${Date.now() - startTime}ms`);
        resolve();
      } else if (Date.now() - startTime >= maxWaitTime) {
        console.warn(`Google Maps API failed to load within ${maxWaitTime}ms timeout`);
        console.warn('Window.google:', typeof window !== 'undefined' ? (window as any).google : 'N/A');
        reject(new Error('Google Maps API failed to load within timeout'));
      } else {
        setTimeout(checkGoogleMaps, checkInterval);
      }
    };

    checkGoogleMaps();
  });
};

/**
 * Calculate road distance using server-side Google Maps Distance Matrix API as fallback
 * @param origin - Start coordinates
 * @param destination - End coordinates
 * @returns Promise with distance and duration information
 */
const calculateRoadDistanceServer = async (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<{ distance: number; duration: number } | null> => {
  try {
    const origins = `${origin.lat},${origin.lng}`;
    const destinations = `${destination.lat},${destination.lng}`;
    
    const url = `/api/distance-matrix?origins=${encodeURIComponent(origins)}&destinations=${encodeURIComponent(destinations)}`;
    console.log('Calling server-side distance API:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // Include cookies for session
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `Server returned ${response.status}` };
      }
      console.error('Server API error response:', response.status, errorData);
      throw new Error(`Server API returned ${response.status}: ${errorData.error || 'Unknown error'}`);
    }
    
    const data = await response.json();
    console.log('Server API response:', data);
    
    // Check if the response has an error field (from our API)
    if (data.error) {
      throw new Error(data.error);
    }
    
    if (data.status === 'OK' && data.rows && data.rows[0] && data.rows[0].elements && data.rows[0].elements[0]) {
      const element = data.rows[0].elements[0];
      
      if (element.status === 'OK' && element.distance && element.duration) {
        const distanceInMeters = element.distance.value;
        const durationInSeconds = element.duration.value;
        
        // Convert to kilometers and minutes
        const distanceInKm = distanceInMeters / 1000;
        const durationInMinutes = durationInSeconds / 60;
        
        console.log('Successfully calculated distance via server API:', distanceInKm, 'km');
        return {
          distance: Math.round(distanceInKm * 100) / 100, // Round to 2 decimal places
          duration: Math.round(durationInMinutes)
        };
      } else {
        throw new Error(`Distance Matrix API returned element status: ${element.status}`);
      }
    } else {
      throw new Error(`Distance Matrix API returned status: ${data.status || 'UNKNOWN'}`);
    }
  } catch (error) {
    console.error('Server-side distance calculation failed:', error);
    if (error instanceof Error) {
      console.error('Error details:', error.message);
    }
    return null;
  }
};

/**
 * Calculate road distance using Google Maps Directions Service
 * @param origin - Start coordinates
 * @param destination - End coordinates
 * @returns Promise with distance and duration information
 */
export const calculateRoadDistance = async (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<{ distance: number; duration: number } | null> => {
  // Try client-side Google Maps API first - wait longer since server-side might not have API key
  let useClientSide = false;
  try {
    // Wait for Google Maps API to be loaded (longer timeout since server might not have key)
    await waitForGoogleMaps(8000); // Wait up to 8 seconds for client-side API
    useClientSide = true;
    console.log('Client-side Google Maps API is available, using it for distance calculation');
  } catch (error) {
    console.warn('Google Maps API not loaded on client-side, will try server-side API...');
    useClientSide = false;
  }

  // If client-side is available, try it first
  if (useClientSide) {
    try {
      // Get google object from window or global - prioritize window.google as that's how @react-google-maps/api loads it
      let googleMaps = null;
      if (typeof window !== 'undefined' && (window as any).google) {
        googleMaps = (window as any).google;
        console.log('Using window.google for Directions Service');
      } else if (typeof google !== 'undefined') {
        googleMaps = google;
        console.log('Using global google for Directions Service');
      }
      
      if (!googleMaps || !googleMaps.maps || !googleMaps.maps.DirectionsService) {
        console.error('Google Maps object:', googleMaps);
        console.error('Google Maps.maps:', googleMaps?.maps);
        throw new Error('Directions Service not available');
      }

      console.log('Creating Directions Service...');
      const service = new googleMaps.maps.DirectionsService();
    
      const request = {
        origin: new googleMaps.maps.LatLng(origin.lat, origin.lng),
        destination: new googleMaps.maps.LatLng(destination.lat, destination.lng),
        travelMode: googleMaps.maps.TravelMode.DRIVING
      };

      return new Promise((resolve, reject) => {
        service.route(request, (response: any, status: any) => {
          if (status === 'OK' && response) {
            const route = response.routes[0];
            if (route && route.legs[0]) {
              const leg = route.legs[0];
              if (leg.distance && leg.duration) {
                const distanceInMeters = leg.distance.value;
                const durationInSeconds = leg.duration.value;
                
                // Convert to kilometers and minutes
                const distanceInKm = distanceInMeters / 1000;
                const durationInMinutes = durationInSeconds / 60;
                
                console.log('Successfully calculated distance via client-side Google Maps:', distanceInKm, 'km');
                resolve({
                  distance: Math.round(distanceInKm * 100) / 100, // Round to 2 decimal places
                  duration: Math.round(durationInMinutes)
                });
              } else {
                console.warn('Route leg missing distance or duration, trying server-side API...');
                // Try server-side API as fallback
                calculateRoadDistanceServer(origin, destination)
                  .then(serverResult => {
                    if (serverResult) {
                      resolve(serverResult);
                    } else {
                      reject(new Error('Unable to get distance from route leg and server-side API failed'));
                    }
                  })
                  .catch(() => reject(new Error('Unable to get distance from route leg')));
              }
            } else {
              console.warn('No route found, trying server-side API...');
              // Try server-side API as fallback
              calculateRoadDistanceServer(origin, destination)
                .then(serverResult => {
                  if (serverResult) {
                    resolve(serverResult);
                  } else {
                    reject(new Error('No route found and server-side API failed'));
                  }
                })
                .catch(() => reject(new Error('No route found')));
            }
          } else {
            console.warn('Directions API error:', status, '- trying server-side API...');
            // Try server-side API as fallback
            calculateRoadDistanceServer(origin, destination)
              .then(serverResult => {
                if (serverResult) {
                  resolve(serverResult);
                } else {
                  reject(new Error(`Directions API returned ${status} and server-side API failed`));
                }
              })
              .catch(() => reject(new Error(`Directions API returned ${status}`)));
          }
        });
      });
    } catch (error) {
      console.warn('Client-side Directions Service failed, trying server-side API...', error);
      // Fall through to server-side API
    }
  }

  // Use server-side API (either as primary if client-side not available, or as fallback)
  console.log('Attempting to use server-side Google Maps API for distance calculation...');
  const serverResult = await calculateRoadDistanceServer(origin, destination);
  if (serverResult) {
    return serverResult;
  }
  
  // If server-side also fails, check if we have a client-side API key configured
  const hasClientApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (hasClientApiKey) {
    // If we have a client API key, try waiting longer for it to load
    console.warn('Server-side API failed, but client API key exists. Waiting longer for client-side API...');
    try {
      await waitForGoogleMaps(5000); // Wait another 5 seconds
      const googleMaps = (typeof window !== 'undefined' && (window as any).google) || (typeof google !== 'undefined' ? google : null);
      if (googleMaps && googleMaps.maps && googleMaps.maps.DirectionsService) {
        const service = new googleMaps.maps.DirectionsService();
        const request = {
          origin: new googleMaps.maps.LatLng(origin.lat, origin.lng),
          destination: new googleMaps.maps.LatLng(destination.lat, destination.lng),
          travelMode: googleMaps.maps.TravelMode.DRIVING
        };
        
        return new Promise((resolve, reject) => {
          service.route(request, (response: any, status: any) => {
            if (status === 'OK' && response && response.routes && response.routes[0] && response.routes[0].legs && response.routes[0].legs[0]) {
              const leg = response.routes[0].legs[0];
              if (leg.distance && leg.duration) {
                const distanceInKm = leg.distance.value / 1000;
                const durationInMinutes = leg.duration.value / 60;
                console.log('Successfully calculated distance via client-side Google Maps (retry):', distanceInKm, 'km');
                resolve({
                  distance: Math.round(distanceInKm * 100) / 100,
                  duration: Math.round(durationInMinutes)
                });
              } else {
                reject(new Error('Route leg missing distance or duration'));
              }
            } else {
              reject(new Error(`Directions API returned ${status}`));
            }
          });
        });
      }
    } catch (retryError) {
      console.error('Client-side API retry also failed:', retryError);
    }
  }
  
  // If both fail, throw error
  throw new Error('Unable to calculate road distance: Both client-side and server-side Google Maps APIs failed. Please ensure Google Maps API is configured.');
};

// Fallback calculation using straight-line distance with a road factor
const calculateFallbackDistance = (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): { distance: number; duration: number } => {
  // Calculate straight-line distance
  const straightLineDistance = calculateStraightLineDistance(origin.lat, origin.lng, destination.lat, destination.lng);
  
  if (!straightLineDistance) {
    return { distance: 0, duration: 0 };
  }
  
  // Apply a road factor (typically 1.1 to 1.3 for rural roads)
  // This gives a reasonable approximation of road distance
  const roadFactor = 1.2; // 20% longer than straight-line for rural roads
  const estimatedRoadDistance = straightLineDistance * roadFactor;
  
  // Estimate travel time (assuming 60 km/h average speed on rural roads)
  const averageSpeedKmh = 60;
  const estimatedDurationMinutes = (estimatedRoadDistance / averageSpeedKmh) * 60;
  
  return {
    distance: Math.round(estimatedRoadDistance * 100) / 100, // Round to 2 decimal places
    duration: Math.round(estimatedDurationMinutes)
  };
};

/**
 * Calculate the straight-line distance between two GPS coordinates using the Haversine formula
 * @param lat1 - Start latitude
 * @param lng1 - Start longitude
 * @param lat2 - End latitude
 * @param lng2 - End longitude
 * @returns Distance in kilometers
 */
export const calculateStraightLineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate road distance (primary) and straight-line distance (reference) between two GPS coordinates
 * @param startLat - Start latitude
 * @param startLng - Start longitude
 * @param endLat - End latitude
 * @param endLng - End longitude
 * @returns Promise with distance calculations (road distance prioritized)
 */
export const calculateDistances = async (
  startLat: number,
  startLng: number,
  endLat: number,
  endLng: number
): Promise<DistanceResult> => {
  // Calculate straight-line distance (for reference)
  const straightLineDistance = calculateStraightLineDistance(startLat, startLng, endLat, endLng);
  
  // Calculate road distance (primary) - this will use Google Maps API
  let roadResult: { distance: number; duration: number } | null = null;
  let error: string | undefined = undefined;
  
  try {
    roadResult = await calculateRoadDistance(
      { lat: startLat, lng: startLng },
      { lat: endLat, lng: endLng }
    );
    
    if (!roadResult) {
      error = 'Unable to calculate road distance. Please ensure Google Maps API is available.';
    }
  } catch (err) {
    console.error('Error calculating road distance:', err);
    error = err instanceof Error ? err.message : 'Failed to calculate road distance from Google Maps';
    
    // Only use fallback as absolute last resort if both client and server APIs fail
    // and show a warning to the user
    console.warn('All Google Maps APIs failed, using fallback calculation as last resort');
    const fallbackResult = calculateFallbackDistance(
      { lat: startLat, lng: startLng },
      { lat: endLat, lng: endLng }
    );
    roadResult = fallbackResult;
    error = `${error}. Using estimated distance (may be inaccurate).`;
  }
  
  return {
    roadDistance: roadResult?.distance,
    roadDuration: roadResult?.duration,
    straightLineDistance,
    error
  };
};

/**
 * Format distance for display (always in km)
 * @param distance - Distance in kilometers
 * @returns Formatted distance string
 */
export const formatDistance = (distance: number): string => {
  return `${distance} km`;
};

/**
 * Format duration for display
 * @param duration - Duration in minutes
 * @returns Formatted duration string
 */
export const formatDuration = (duration: number): string => {
  if (duration < 60) {
    return `${duration} min`;
  } else {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}; 